import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/utils/supabase/client";
import { useAuth } from "@/contexts/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, ArrowLeft, Hospital, Shield, Zap, Users } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const isLoggingIn = useRef(false);

  // Redirect if already logged in (only when not actively logging in via form)
  useEffect(() => {
    if (user && profile && !isLoggingIn.current) {
      if (profile.role === 'admin') {
        navigate("/admin/dashboard");
      } else {
        navigate("/app/dashboard");
      }
    }
  }, [user, profile, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    isLoggingIn.current = true;

    try {
      console.log("Attempting login with:", { email, password: password.length > 0 ? "[HIDDEN]" : "[EMPTY]" });

      if (!email || !password) {
        throw new Error("Please fill in all fields");
      }

      // Authenticate with Supabase
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password
      });

      if (authError) {
        console.error("Supabase auth error:", authError);
        throw new Error(authError.message);
      }

      if (!data.user) {
        throw new Error("No user returned from authentication");
      }

      console.log("Login successful, user:", data.user.email);

      // Check the user's profile role
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("role")
        .eq("id", data.user.id)
        .single();

      if (profileError) {
        console.error("Error checking profile:", profileError);
      }

      // Route admin users to admin dashboard
      if (profileData?.role === 'admin') {
        console.log("Admin user detected, redirecting to admin dashboard");
        navigate("/admin/dashboard");
        return;
      }

      // Check if user has an organization
      const { data: orgData, error: orgError } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("owner_id", data.user.id);

      if (orgError) {
        console.error("Error checking organization:", orgError);
      }

      if (orgData && orgData.length > 0) {
        console.log("User has organization:", orgData[0].name);
        navigate("/app/dashboard");
      } else {
        console.log("User has no organization, redirecting to onboarding");
        navigate("/app/onboarding");
      }

    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "An error occurred during login");
      isLoggingIn.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    console.log("Email changed to:", value);
    setEmail(value);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    console.log("Password changed, length:", value.length);
    setPassword(value);
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50">
      {/* LEFT */}
      <section className="flex flex-1 flex-col items-center justify-center p-8">
        <Link
          to="/"
          className="mb-8 flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-sm font-medium">Back to Home</span>
        </Link>

        <div className="flex items-center justify-center gap-3 mb-8">
          <Hospital className="h-10 w-10 text-blue-600" />
          <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Clinic365
          </span>
        </div>

        <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white/90 shadow-2xl backdrop-blur-lg">
          <div className="absolute inset-0 -z-10 rounded-[inherit] bg-gradient-to-r from-blue-500/10 to-indigo-500/10" />
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-900">Welcome Back</CardTitle>
            <p className="mt-1 text-sm text-gray-600">Sign in to your Clinic365 account</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="doctor@clinic.com"
                  value={email}
                  onChange={handleEmailChange}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={handlePasswordChange}
                    required
                    autoComplete="current-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <input
                    id="remember"
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <label htmlFor="remember" className="text-sm text-gray-600">
                    Remember me
                  </label>
                </div>
                <Link
                  to="/auth/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Forgot password?
                </Link>
              </div>

              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Signing in...
                  </span>
                ) : (
                  "Sign In"
                )}
              </Button>

              <p className="text-center text-gray-600">
                Don't have an account?{" "}
                <Link
                  to="/auth/signup"
                  className="font-medium text-blue-600 hover:text-blue-700"
                >
                  Sign up
                </Link>
              </p>
            </form>
          </CardContent>
        </div>

        <div className="mt-8 flex items-center gap-8 text-sm text-gray-500">
          <div className="flex items-center gap-2"><Shield className="h-4 w-4" /> HIPAA&nbsp;Compliant</div>
          <div className="flex items-center gap-2"><Zap className="h-4 w-4" /> 99.9%&nbsp;Uptime</div>
          <div className="flex items-center gap-2"><Users className="h-4 w-4" /> 10K+&nbsp;Clinics</div>
        </div>
      </section>

      {/* RIGHT */}
      <section className="hidden lg:flex flex-1 bg-gradient-to-br from-blue-600 to-indigo-700 items-center justify-center p-12">
        <div className="max-w-md text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Welcome Back!</h2>
          <p className="text-lg text-blue-100 mb-8">
            Continue managing your clinic with our comprehensive healthcare management platform.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-left">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Hospital className="h-4 w-4" />
              </div>
              <span>Manage patient records securely</span>
            </div>
            <div className="flex items-center gap-3 text-left">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Users className="h-4 w-4" />
              </div>
              <span>Schedule appointments efficiently</span>
            </div>
            <div className="flex items-center gap-3 text-left">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <Zap className="h-4 w-4" />
              </div>
              <span>Track payments and expenses</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}